<template>
  <v-card max-width="350">
    <v-list>
      <v-list-item>
        <v-list-item-avatar size="64">
          <v-img :src="profilePicture"></v-img>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ username }}</v-list-item-title>
          <v-list-item-subtitle>{{ email }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon @click="close" v-on="on">
                <v-icon>
                  {{ mdiCloseCircle }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.close') }}</span>
          </v-tooltip>

          <v-tooltip top v-if="isAdmin">
            <template v-slot:activator="{ on }">
              <v-btn icon @click="openSettings" v-on="on">
                <v-icon>
                  {{ profileIcon }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.map.criticalPopup.sections.titles.settings') }}</span>
          </v-tooltip>

        </v-list-item-action>
      </v-list-item>
      <v-subheader class="font-weight-regular text-center d-flex justify-center">{{
        $vuetify.lang.t('$vuetify.statisticsCard.title') }}
      </v-subheader>
      <v-list-item dense>
        <v-row no-gutters>
          <v-col cols="12">
            <v-simple-table dense>
              <tbody>
              <tr v-for="item in driverStatistics.statisticsState()" :key="item.icon">
                <td>
                  <v-icon left>{{ item.icon }}</v-icon>
                  {{ $vuetify.lang.t(item.title) }}
                </td>
                <td><span v-if=" typeof item.value !== 'string' ">{{ valueDisplay(item.value) }}</span>
                  <span v-else>{{ item.value }}</span> <span v-if=" 'postfix' in item"> {{  $vuetify.lang.t(item.postfix) }}</span>
                </td>
              </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-list-item>
      <v-list-item dense class="mt-2" v-if="cardType === UserProfileType.AUTH">
        <v-btn :to="{name: 'panelMap'}" target="_blank" exact tile text color="green" @click="close">
          <v-icon left>{{ mdiCarHatchback }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.adminPanel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn :to="{name: 'panelCharts'}" target="_blank" exact tile text color="purple" @click="close">
          <v-icon left>{{ chartsIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.charts') }}
        </v-btn>
      </v-list-item>
      <v-list-item dense v-if="cardType === UserProfileType.AUTH">
        <v-btn :to="{name: 'panelReports'}" target="_blank" exact tile text color="orange" @click="close">
          <v-icon left>{{ reportsIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.reports') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn :to="{name: 'panelDataset'}" target="_blank" exact tile text color="blue" @click="close">
          <v-icon left>{{ reportsIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.dataset') }}
        </v-btn>
      </v-list-item>
      <v-list-item dense v-if="cardType === UserProfileType.AUTH && isAdmin">
        <v-btn :to="{name: 'carParks'}" target="_blank" exact tile text color="yellow darken-4" @click="close">
          <v-icon left>{{ carParksIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.carParks') }}
        </v-btn>
      </v-list-item>
      <v-list-item dense v-if="cardType === UserProfileType.AUTH">
        <v-spacer></v-spacer>
        <v-btn tile text color="red" @click="logout" :loading="!isInit" :disabled="!isInit">
          <v-icon left>{{ mdiLogout }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.auth.signOut') }}
        </v-btn>
      </v-list-item>
    </v-list>

    <driver-settings-dialog v-if="isAdmin"
                            :dialog="settingsDialog" :profile="profile" :user-id="profile.userId"
                            :loaded="isLoaded"
                            :settings="settings" :username="username"
                            :is-default-settings="false"
                            @close-settings-dialog="settingsDialog = false"></driver-settings-dialog>
  </v-card>
</template>

<script>
import { UserProfileType } from '@/enum'
import {
  mdiAccountBox,
  mdiCarHatchback,
  mdiChartBarStacked,
  mdiCloseCircle,
  mdiFileChart,
  mdiLogout,
  mdiMapMarkerDistance,
  mdiTimer, mdiTrainCar
} from '@mdi/js'
import { UserStatistics } from '@/models/user/UserStatistics'

import backend from '../../backend'
import { UserProfile } from '@/models/user/UserProfile'
import { UserSettings } from '@/models/user/UserSettings'
import DriverSettingsDialog from '../map/driverSettingsDialog'
import { createHelpers } from 'vuex-map-fields'
import { AUTH_MODULE } from '@/store/modules'

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

export default {
  name: 'profileCard',
  components: { DriverSettingsDialog },
  props: {
    cardType: {
      type: String,
      default: UserProfileType.AUTH
    },
    profilePicture: {
      type: String,
      default: ''
    },
    username: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    },
    statistics: {
      type: Object,
      default: () => new UserStatistics()
    },
    isInit: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...authFields(['isAdmin']),
    driverStatistics () {
      if (!(this.statistics instanceof UserStatistics)) {
        return new UserStatistics(this.statistics)
      } else {
        return this.statistics
      }
    }
  },
  data: () => ({
    settingsDialog: false,
    isLoaded: false,
    profile: new UserProfile(),
    settings: new UserSettings(),

    mdiLogout: mdiLogout,
    mdiCloseCircle: mdiCloseCircle,
    mdiCarHatchback: mdiCarHatchback,
    mdiMapMarkerDistance: mdiMapMarkerDistance,
    mdiTimer: mdiTimer,
    profileIcon: mdiAccountBox,
    chartsIcon: mdiChartBarStacked,
    reportsIcon: mdiFileChart,
    carParksIcon: mdiTrainCar
  }),
  methods: {
    close () {
      this.$emit('close')
    },
    logout () {
      this.$emit('logout')
    },
    valueDisplay (value) {
      const absValue = Math.abs(value)
      if (absValue === 0) {
        return value
      } else if (absValue >= 1.0) {
        return value.toFixed(0)
      } else {
        return value.toFixed(3)
      }
    },
    async openSettings () {
      this.settingsDialog = true
      await this._fetchProfile()
    },
    async _fetchProfile () {
      this.isLoaded = false
      try {
        const response = await backend.fetchDriverProfile({})

        this.profile = new UserProfile(response.data.profile)
        this.settings = new UserSettings(response.data.settings)
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoaded = true
      }

    },
  },
  created () {
    this['UserProfileType'] = UserProfileType
  }
}
</script>

<style scoped>
  .v-list-item--dense, .v-list--dense .v-list-item {
    min-height: 20px !important;
  }

  .v-list-item__title {
    white-space: pre-line;
  }

  .v-subheader {
    height: 25px;
  }
</style>
